import React from 'react';
import type { ExteriorFinishData } from '../../types/exteriorFinish';
import { colorNameToCss, getExteriorFinishPresentation } from '../../utils/exteriorFinish';

interface ExteriorFinishDisplayProps {
  data: ExteriorFinishData;
  compact?: boolean;
  className?: string;
}

const ExteriorFinishDisplay: React.FC<ExteriorFinishDisplayProps> = ({
  data,
  compact = false,
  className = '',
}) => {
  const presentation = getExteriorFinishPresentation(data);
  if (!presentation) {
    return null;
  }

  const swatchCss = colorNameToCss(presentation.swatchLabel);

  if (compact) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        {swatchCss && (
          <div
            className="w-4 h-4 rounded-full border-2 border-white/[0.14] shadow-sm shrink-0"
            style={{ backgroundColor: swatchCss }}
          />
        )}
        <span className="text-gray-400 text-sm font-medium">{presentation.primaryText}</span>
        {presentation.badgeLabel && (
          <span className="text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded-full bg-orange-500/20 text-orange-300 border border-orange-500/30">
            {presentation.badgeLabel}
          </span>
        )}
      </div>
    );
  }

  return (
    <div
      className={`rounded-xl border border-white/[0.07] bg-[#0c0c0c]/50 px-4 py-3 ${className}`}
    >
      <div className="flex items-start gap-3">
        {swatchCss && (
          <div
            className="w-10 h-10 rounded-lg border-2 border-white/[0.10] shadow-inner shrink-0 mt-0.5"
            style={{ backgroundColor: swatchCss }}
          />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            {presentation.badgeLabel && (
              <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-orange-500/20 text-orange-300 border border-orange-500/30">
                {presentation.badgeLabel}
              </span>
            )}
            {!presentation.badgeLabel && (
              <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-md bg-[#111]/80 text-gray-300 border border-white/[0.10]">
                Exterior
              </span>
            )}
          </div>
          <p className="mt-1 text-white font-semibold leading-snug">{presentation.primaryText}</p>
          {presentation.secondaryText && (
            <p className="mt-1 text-sm text-gray-400">{presentation.secondaryText}</p>
          )}
          {presentation.detailText && (
            <p className="mt-1 text-xs text-orange-300/90">{presentation.detailText}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExteriorFinishDisplay;
